import { ref } from "vue";
import { cartQuantityChanged } from "@common/analytics";
import { EventPriceDto } from "@/models.g";
import {
  TicketPurchaseDtoViewModel,
  PublicPurchaseServiceViewModel,
} from "@/viewmodels.g";
import { setupPurchaseReloadHandling } from "@common/utils";

/**
 * Composable for handling unseated GA ticket quantity changes.
 * @param purchase The purchase view model (reactive).
 * @param service The purchase service.
 */
export function useUnseatedTicketChange(
  purchase: TicketPurchaseDtoViewModel,
  service: PublicPurchaseServiceViewModel,
  refreshLocalQuantity: () => void,
) {
  const updatingUnseatedGATickets = ref(new Set<number>());

  function setupUnseatedTicketChange() {
    service.setTicketQuantity.setConcurrency?.("debounce");
    setupPurchaseReloadHandling(purchase, service.setTicketQuantity);
    refreshLocalQuantity();
  }

  function isUpdatingUnseatedGA(eventPriceId?: number): boolean {
    return eventPriceId
      ? updatingUnseatedGATickets.value.has(eventPriceId)
      : false;
  }

  async function handleUnseatedGAQuantityUpdate(
    eventPriceId: number,
    input: number | string,
    localQuantity: number,
    eventPrice?: EventPriceDto,
  ) {
    updatingUnseatedGATickets.value.add(eventPriceId);
    let num = parseInt(input.toString());
    const oldLocalQuantity = localQuantity;
    if (!eventPrice) {
      eventPrice = purchase.eventDate?.eventPrices?.find(
        (p) => p.id === eventPriceId,
      )!;
    }
    const minimumQuantity = eventPrice.minimumQuantity ?? 0;
    const maximumPurchaseQuantity = eventPrice.maximumPurchaseQuantity!;

    // If we're an agent, we probably want to skip over some of these checks?

    if (isNaN(num)) {
      num = 0;
    } else if (num < minimumQuantity) {
      // If the users decrements below the minimum quantity, set to zero.
      // If the user increments but are below the minimum quantity, set it to the minimum quantity.
      num = num < oldLocalQuantity ? 0 : minimumQuantity;
    } else if (maximumPurchaseQuantity && num > maximumPurchaseQuantity) {
      num = maximumPurchaseQuantity;
    }

    try {
      await service.setTicketQuantity(purchase.publicId, eventPrice.id, num);

      cartQuantityChanged(purchase, eventPrice, num - oldLocalQuantity);
    } catch (err) {
      console.error(err);
      refreshLocalQuantity();
    } finally {
      updatingUnseatedGATickets.value.delete(eventPriceId!);
    }
  }

  return {
    handleUnseatedGAQuantityUpdate,
    isUpdatingUnseatedGA,
    updatingUnseatedGATickets,
    setupUnseatedTicketChange,
  };
}
